import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { TranslationService } from '../../../../core/i18n';
import {
  SOLR_SEACH_SORTING_ASC_PREFIX,
  SOLR_SEACH_SORTING_DESC_PREFIX,
  SolrSearchResult,
  SolrSearchSortingDefaults,
} from '../../../../core/model';
import { CatalogSearchService, CatalogTabTypes, Sort, SortPrefix } from '../../container/catalog/catalog-search.service';

@Component({
  selector: 'py-catalog-sort',
  templateUrl: './catalog-sort.component.html',
  styleUrls: ['./catalog-sort.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CatalogSortComponent implements OnInit {
  @Input() searchResults$: Observable<SolrSearchResult>;
  @Input() activeTabIndex$: Observable<CatalogTabTypes>;
  @Input() sortOptionToHide?: string;

  sorts$: Observable<Sort[]>;

  subscription = new Subscription();

  formGroup = new UntypedFormGroup({
    sort: new UntypedFormControl('', []),
    sortOrder: new UntypedFormControl(SOLR_SEACH_SORTING_ASC_PREFIX, []),
    pageSize: new UntypedFormControl('10', []),
  });

  get isDirectionDesc(): boolean {
    return this.formGroup.get('sortOrder').value === SOLR_SEACH_SORTING_DESC_PREFIX;
  }

  get directionRevertedPrefix(): string {
    return this.isDirectionDesc ? SOLR_SEACH_SORTING_ASC_PREFIX : SOLR_SEACH_SORTING_DESC_PREFIX;
  }

  revertOrderDisabled: boolean;

  constructor(
    private catalogSearchService: CatalogSearchService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.sorts$ = combineLatest([this.searchResults$, this.activeTabIndex$]).pipe(
      filter(([searchResults]) => !!searchResults?.sorts),
      map(([searchResults, activeTabIndex]) => {
        const prefix = activeTabIndex === CatalogTabTypes.Products ? SortPrefix.Product : SortPrefix.Article;

        return {
          sorts: searchResults.sorts
            .filter(
              (sort) =>
                sort.code.toLowerCase().substring(0, 2) === prefix ||
                sort.code === SolrSearchSortingDefaults.PDP_TYPE_SKU_PICKER ||
                (activeTabIndex === CatalogTabTypes.Articles && sort.code === SolrSearchSortingDefaults.PDP_TYPE_ARTICLES_PRICE)
            )
            .map(
              (sort) =>
                ({
                  value: sort.code,
                  label: sort.name,
                  selected: sort.selected,
                }) as Sort
            ),
          searchResults,
          activeTabIndex,
        };
      }),
      switchMap(({ sorts, searchResults, activeTabIndex }) => {
        if (activeTabIndex !== CatalogTabTypes.Articles) {
          return of(sorts);
        }
        // add price sorting, if enabled
        const sortByPriceLabel$ = this.translationService.translate('catalog.sortByPrice_heading');
        const sortByPriceTooManyResultsHint$ = this.translationService.translate('catalog.sortByPriceTooManyResults_hint');
        return combineLatest([
          of(sorts),
          this.catalogSearchService.enablePriceSorting$,
          this.catalogSearchService.priceSortThreshold$,
          sortByPriceLabel$,
          sortByPriceTooManyResultsHint$,
        ]).pipe(
          map(([sorts, enablePriceSorting, priceSortThreshold, sortByPriceLabel, sortByPriceTooManyResultsHint]) => {
            if (enablePriceSorting) {
              const disabled = searchResults.pagination?.totalResults > priceSortThreshold;
              const sortEntry =
                sorts.find((sort) => sort.value === SolrSearchSortingDefaults.PDP_TYPE_ARTICLES_PRICE) ||
                ({
                  value: SolrSearchSortingDefaults.PDP_TYPE_ARTICLES_PRICE,
                  label: sortByPriceLabel,
                  selected: false,
                } as Sort);
              return [
                ...sorts.filter((sort) => sort.value !== SolrSearchSortingDefaults.PDP_TYPE_ARTICLES_PRICE),
                {
                  ...sortEntry,
                  label: sortByPriceLabel,
                  disabled,
                  description: disabled ? sortByPriceTooManyResultsHint : undefined,
                  pageSize: priceSortThreshold,
                } as Sort,
              ];
            }
            return sorts;
          })
        );
      }),
      map((sorts) => {
        const active = (sorts || []).find((s) => s.selected) ?? sorts?.[0];
        if (!active) {
          return;
        }
        this.formGroup.get('sort').setValue(active.value, { emitEvent: false });

        //set sort order
        const sortOrderValue = active.value.includes(SOLR_SEACH_SORTING_DESC_PREFIX)
          ? SOLR_SEACH_SORTING_DESC_PREFIX
          : SOLR_SEACH_SORTING_ASC_PREFIX;
        const filterOrderValue = active.value.includes(SOLR_SEACH_SORTING_DESC_PREFIX)
          ? SOLR_SEACH_SORTING_ASC_PREFIX
          : SOLR_SEACH_SORTING_DESC_PREFIX;
        this.formGroup.get('sortOrder').setValue(sortOrderValue, { emitEvent: false });

        this.revertOrderDisabled = this.isRevertOrderDisabled(sorts, active.value);

        //When creating sort expressions in backoffice, both asc and desc needs to be created in order to toggle search order. But in the gui we only show one of them (either asc or desc depending on whats selected)
        return sorts.filter((sort) => !sort.value?.includes(filterOrderValue));
      }),
      // Relevance (score) sort should not be displayed on PDP variant with articles list
      map((sorts) => (this.sortOptionToHide ? sorts.filter((sort) => sort.value !== this.sortOptionToHide) : sorts))
    );

    this.subscription.add(
      this.formGroup.get('sort').valueChanges.subscribe((value) => {
        this.catalogSearchService.sort(value);
      })
    );

    this.subscription.add(
      this.formGroup.get('sortOrder').valueChanges.subscribe(() => {
        const sort = this.formGroup.get('sort')?.value;

        if (!sort?.includes(SOLR_SEACH_SORTING_DESC_PREFIX) && !sort?.includes(SOLR_SEACH_SORTING_ASC_PREFIX)) {
          return false;
        }

        this.catalogSearchService.sort(this.getNewSortOrderValue(sort));
      })
    );
  }

  revertSortOrder(): void {
    this.formGroup.get('sortOrder').setValue(this.directionRevertedPrefix);
  }

  getNewSortOrderValue(activeSortValue: string): string {
    const newSortOrderValue = activeSortValue?.includes(SOLR_SEACH_SORTING_DESC_PREFIX)
      ? SOLR_SEACH_SORTING_ASC_PREFIX
      : SOLR_SEACH_SORTING_DESC_PREFIX;
    return activeSortValue
      .replace(SOLR_SEACH_SORTING_DESC_PREFIX, newSortOrderValue)
      .replace(SOLR_SEACH_SORTING_ASC_PREFIX, newSortOrderValue);
  }

  isRevertOrderDisabled(sorts: Sort[], activeSortValue: string): boolean {
    const newSort = this.getNewSortOrderValue(activeSortValue);

    if (!newSort.includes(SOLR_SEACH_SORTING_DESC_PREFIX) && !newSort.includes(SOLR_SEACH_SORTING_ASC_PREFIX)) {
      return true;
    }

    return !sorts?.find((sort) => sort.value === newSort);
  }
}
